import React, { useState, useEffect } from "react";
import {
  TextField,
  Box,
  Typography,
  Chip,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  IconButton,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { Add, ExpandMore, Close, Edit } from "@mui/icons-material"; // Import Edit icon
import "./EditCertificate.css";
import { useDispatch, useSelector } from "react-redux";
import { setProfileState } from "../../../actions/MyTechProfile/myTechProfileActions";
// Make sure to import your action
// import { setProfileState } from "../../../actions/MyTechProfile/myTechProfileActions";

// --- Mock Data ---
const allSkills = [
  { id: 1, name: "React" },
  { id: 2, name: "Node.js" },
  { id: 3, name: "JavaScript" },
  { id: 4, name: "Python" },
];

// --- Custom Theme ---
const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#8a2be2", // Violet
    },
    background: {
      paper: "#333",
    },
    text: {
      primary: "#fff",
    },
  },
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "&.Mui-focused fieldset": {
              borderColor: "#fff",
            },
          },
        },
      },
    },
  },
});

// --- The Main Component ---
const EditCertificate = () => {
  // Mock Redux for demonstration if not available
  const dispatch = useDispatch();
  const { profile } = useSelector(
    (state) => state.editMyTechProfileStore || { profile: { certificates: [] } }
  );

  const [certificates, setCertificates] = useState(profile?.certificates || []);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState(null); // State to track the certificate being edited
  const [formCertificate, setFormCertificate] = useState({
    title: "",
    image: "",
    details: "",
    techStack: [],
  });

  // Update local state if profile from Redux changes
  useEffect(() => {
    setCertificates(profile?.certificates || []);
  }, [profile?.certificates]);

  const handleOpenEditForm = (cert) => {
    setEditingCertificate(cert);
    setFormCertificate({ ...cert });
    setIsFormOpen(true);
  };

  const handleOpenAddForm = () => {
    setEditingCertificate(null);
    setFormCertificate({ title: "", image: "", details: "", techStack: [] });
    setIsFormOpen(true);
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingCertificate(null);
  };

  const handleSave = () => {
    if (!formCertificate.title) return;

    let updatedCertificates;

    if (editingCertificate) {
      // Logic for editing an existing certificate
      updatedCertificates = certificates.map((cert) =>
        cert.id === editingCertificate.id
          ? { ...cert, ...formCertificate }
          : cert
      );
    } else {
      // Logic for adding a new certificate
      const newCertificate = {
        id: Date.now(),
        ...formCertificate,
        certificateIssuedByAcetechies: false,
      };
      updatedCertificates = [...certificates, newCertificate];
    }

    setCertificates(updatedCertificates);
    dispatch(
      setProfileState({ ...profile, certificates: updatedCertificates })
    );
    handleFormClose();
  };

  const handleRemove = (certId) => {
    const updatedCertificates = certificates.filter(
      (cert) => cert.id !== certId
    );
    setCertificates(updatedCertificates);
    dispatch(
      setProfileState({ ...profile, certificates: updatedCertificates })
    );
  };

  return (
    <ThemeProvider theme={theme}>
      <div className="edit-certificate-container">
        <Typography className="edit-certificate-title">
          Edit Certificate
        </Typography>

        <div className="certificates-list">
          {certificates.map((cert) => (
            <Accordion key={cert.id} className="certificate-accordion">
              <AccordionSummary expandIcon={<ExpandMore />}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemove(cert.id);
                  }}
                  className="remove-cert-btn"
                >
                  <Close fontSize="small" />
                </IconButton>
                {/* Conditionally render the Edit button */}
                {!cert.certificateIssuedByAcetechies && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenEditForm(cert);
                    }}
                    className="edit-cert-btn"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                )}
                <Typography sx={{ ml: 1, alignSelf: "center" }}>
                  {cert.title}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <img
                  src={cert.image}
                  alt={cert.title}
                  className="certificate-image"
                />
                <Typography
                  sx={{ fontFamily: "Questrial" }}
                  variant="body2"
                  paragraph
                >
                  {cert.details}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {cert.techStack.map((skill) => (
                    <Chip
                      key={skill.id}
                      icon={
                        skill.icon ? (
                          <img
                            src={skill.icon}
                            alt={skill.name}
                            className="skill-icon"
                          />
                        ) : null
                      }
                      label={skill.name}
                      className="tech-stack-chip"
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>

        {!isFormOpen ? (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenAddForm}
            fullWidth
            sx={{
              fontFamily: "Questrial, sans-serif",
              textTransform: "inherit",
              fontSize: "16px",
              backgroundColor: "#ffd700 ",
              "&:hover": {
                backgroundColor: "#ffd700",
              },
              color: "black",
            }}
          >
            Add Certificate
          </Button>
        ) : (
          <Box className="add-certificate-form">
            <Typography variant="h6" gutterBottom>
              {editingCertificate ? "Edit Certificate" : "Add New Certificate"}
            </Typography>
            <TextField
              label="Image URL"
              value={formCertificate.image}
              onChange={(e) =>
                setFormCertificate({
                  ...formCertificate,
                  image: e.target.value,
                })
              }
              fullWidth
              size="small"
              className="input-spacing"
            />
            <TextField
              label="Title"
              value={formCertificate.title}
              onChange={(e) =>
                setFormCertificate({
                  ...formCertificate,
                  title: e.target.value,
                })
              }
              fullWidth
              size="small"
              className="input-spacing"
            />
            <TextField
              label="Details"
              value={formCertificate.details}
              onChange={(e) =>
                setFormCertificate({
                  ...formCertificate,
                  details: e.target.value,
                })
              }
              fullWidth
              multiline
              rows={3}
              size="small"
              className="input-spacing"
            />
            <Autocomplete
              multiple
              options={allSkills}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={formCertificate.techStack}
              onChange={(event, value) =>
                setFormCertificate({ ...formCertificate, techStack: value })
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option.name}
                    size="small"
                    {...getTagProps({ index })}
                    className="tech-stack-chip"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Tech Stack"
                  size="small"
                />
              )}
              className="input-spacing"
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                sx={{ fontFamily: "Questrial" }}
                onClick={handleFormClose}
                size="small"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                size="small"
                sx={{ ml: 1, fontFamily: "Questrial" }}
              >
                Save
              </Button>
            </Box>
          </Box>
        )}
      </div>
    </ThemeProvider>
  );
};

export default EditCertificate;
